import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { fetchProjectActivities } from "../../../api/project_activity";
import { ResourceLoading } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

function activityPrimary(row) {
  return row?.display_title ?? row?.displayTitle ?? row?.title ?? "";
}

function activitySecondary(row) {
  if (!row) return null;
  const num = row.number != null && row.number !== "" ? String(row.number) : null;
  const type = row.activity_type && row.activity_type !== "standard" ? row.activity_type : null;
  if (num && type) return `${num} · ${type}`;
  return num || type;
}

/**
 * Project activity line picker — same UX as {@link ./projectSelector ProjectSelector}
 * (compact trigger, PMDialog dense, edge-to-edge list on small screens).
 */
export const ProjectActivitySelector = (props) => {
  const {
    setFieldValue,
    errors,
    onChange,
    showField,
    project,
    listFetchParams = null,
    noLabel,
  } = props;

  const name = props?.name ?? "project_activity_id";
  const label = props?.label;
  const noneLabelKey = props.noneLabelKey ?? "project_activity.none";

  const { t } = useTranslation();
  const nonePrimary = t(noneLabelKey);

  const fieldLabel =
    label === false ? null : label != null && label !== "" ? label : t("project_activity.label");
  const dialogTitle = fieldLabel ?? t("project_activity.dialog_title");

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const searchInputRef = useAutofocusOnOpen(open);

  const [selected, setSelected] = useState({
    id: null,
    field1: nonePrimary,
    field2: null,
  });

  const selectedFromProps = props?.selected;

  useEffect(() => {
    if (selectedFromProps?.id != null) {
      setSelected({
        id: selectedFromProps.id,
        field1: activityPrimary(selectedFromProps) || nonePrimary,
        field2: activitySecondary(selectedFromProps),
      });
    } else {
      setSelected({
        id: null,
        field1: nonePrimary,
        field2: null,
      });
    }
  }, [selectedFromProps, nonePrimary]);

  const handleOpen = () => {
    if (props.disabled || !project?.id) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const normalizeRow = useCallback((row) => {
    const id = row?.id;
    const primary = activityPrimary(row) || `#${id}`;
    return {
      id,
      value: id,
      primary,
      secondary: activitySecondary(row),
      object: row,
    };
  }, []);

  const handleSelect = (item) => {
    if (item.id == null) {
      setSelected({ id: null, field1: nonePrimary, field2: null });
      if (setFieldValue) {
        setFieldValue(name, null);
        setFieldValue("project_activity", null);
      }
      if (onChange) {
        onChange({ target: { name, value: null, object: null } });
      }
    } else {
      const obj = item.object ?? item;
      setSelected({
        id: item.id,
        field1: item.primary ?? nonePrimary,
        field2: item.secondary ?? null,
      });
      const idStr = obj?.id != null ? String(obj.id) : String(item.id);
      if (setFieldValue) {
        setFieldValue(name, idStr);
        setFieldValue("project_activity", obj);
      }
      if (onChange) {
        onChange({
          target: { name, value: obj?.id ?? item.id, object: obj },
        });
      }
    }
    handleClose();
  };

  useEffect(() => {
    if (!open || !project?.id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchProjectActivities(
      project.id,
      listFetchParams && Object.keys(listFetchParams).length > 0
        ? { params: listFetchParams }
        : {}
    )
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(t("project_activity.load_error"));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, project?.id, listFetchParams, t]);

  const items = useMemo(() => {
    const base = [{ id: null, value: null, primary: nonePrimary, secondary: null, object: null }];
    const mapped = rows.map((row) => normalizeRow(row));
    return [...base, ...mapped];
  }, [rows, nonePrimary, normalizeRow]);

  const filteredItems = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      if (it.id == null) return true;
      const p = (it.primary || "").toLowerCase();
      const s = (it.secondary || "").toLowerCase();
      return p.includes(q) || s.includes(q);
    });
  }, [items, query]);

  const hasSelection = selected?.id != null;
  const isDisabled = props.disabled || !project?.id;
  const showLabelRow = label !== false && noLabel !== false;

  const triggerButton =
    showField !== false ? (
      <div>
        {showLabelRow && fieldLabel && (
          <label className="block text-sm font-medium text-gray-700 mb-1">{fieldLabel}</label>
        )}
        <button
          type="button"
          disabled={isDisabled}
          onClick={handleOpen}
          className={[
            "relative w-full flex items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            isDisabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : !project?.id
              ? "border-gray-200 text-gray-500"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <div className="min-w-0 text-left">
            {!project?.id ? (
              <span className="truncate text-gray-500">{t("project_activity.select_project_first")}</span>
            ) : (
              <>
                <span className="truncate">{selected.field1}</span>
                {selected.field2 && (
                  <span className="ml-2 text-xs text-gray-400 truncate">{selected.field2}</span>
                )}
              </>
            )}
          </div>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
        </button>
        {errors?.[name] && <p className="mt-1 text-xs text-red-600">{errors[name]}</p>}
      </div>
    ) : null;

  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{dialogTitle}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("project_activity.search_placeholder")}
            autoFocus
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <ResourceLoading />
          </div>
        ) : loadError ? (
          <div className="p-6 text-center text-sm text-red-600">{loadError}</div>
        ) : !project?.id ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {t("project_activity.select_project_first")}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">{t("project_activity.empty")}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filteredItems.map((item) => {
              const isSelected =
                item.id == null
                  ? selected?.id == null
                  : String(item.id) === String(selected?.id);
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.primary}</p>
                    {item.secondary && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{item.secondary}</p>
                    )}
                  </div>
                  {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        closeOnOutsideClick
        zIndex={props.zIndex ?? 2600}
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
